import * as React from 'react';

import { Dispatcher as BaseDispatcher } from '../../../../_common/platform/ts/BaseDispatcher';
import { IGlob } from '../Glob';
import { Action } from './AppActions';
import { DialogDefaultSizeType } from '../../../../_common/platform/ts/Interfaces';

export enum DialogType {
    CONFIRM,
    WORKSPACE_DIR_SELECT,
    TEMPLATE_DIR_SELECT,
    CUSTOM,
    FULL_SCREEN,
    NEW_CUSTOM
}

export interface IPopup extends ICommonProperties {
    options?: IPopupOptions;

    close?(): void;
}

export interface IPopupComponentProperties {
    zIndex: number;
    options: IPopupOptions;
    glob?: IGlob;

    close?(): void;
}

export interface IPopupOptions {
    component?: React.ComponentClass<IPopupComponentProperties, any> | ((params) => React.JSX.Element) | React.FC;
    name?: string;
    zIndex?: number;
    top?: number;
    left?: number;
    parentComponentHeight?: number;
    minWidth?: number;
    maxWidth?: number;
    width?: number;

    close?(): void;
}

export interface IDialogConfig {
    name?: string;
    zIndex?: number;
    draggable?: boolean;
    resizable?: boolean;
    minWidth?: number;
    minHeight?: number;
    defaultHeight?: DialogDefaultSizeType;
    defaultWidth?: DialogDefaultSizeType;
    tabID?: number;
}

export interface IDialogConfigTemplateDirSelect extends IDialogConfig {
    selectTemplateMode?: boolean;
    selectTitle?: string;
    nameSelect?: boolean;
    rootId?: number;
    overwrite?: boolean;
    overwriteShow?: boolean;
    allowSelectItem?: boolean;
}

export interface ICommonProperties {
    id: number;
    zIndex: number;
    type: string;
}

export interface IBaseDialog<TDialogConfig extends IDialogConfig> extends ICommonProperties {
    title: string;
    text: string;
    active?: boolean;

    config?: TDialogConfig;

    success?(): JQueryDeferred<any>;
    fail?(): JQueryDeferred<any>;
    cancel?(): void;

    confirmValueGetter?;
    setConfirmValueGetter?;
    unsetConfirmValueGetter?;
}

export interface IDialogWorkspaceDirSelect extends IBaseDialog<IDialogConfigWorkspaceDirSelect> {}

export interface IDialogConfigFullScreen extends IDialogConfig {
    Component: React.ComponentClass<any>;
    props: any;
}

export interface IDialogConfigConfirm extends IDialogConfig {
    confirmTitle?: string;
    cancelTitle?: string;
    content?: string | React.FC | React.ReactNode;
    closeTitle?: string;
}

export interface IDialog extends IBaseDialog<IDialogConfig> {}

export interface IDialogTemplateDirSelect extends IBaseDialog<IDialogConfigTemplateDirSelect> {}

export interface IDialogConfigWorkspaceDirSelect extends IDialogConfig {
    selectTitle?: string;
    nameSelect?: boolean;
    rootId?: number;
    overwrite?: boolean;
    overwriteShow?: boolean;
    allowSelectItem?: boolean;
    allowOpenWorkspace?: boolean;
    modeSelectWorkspace?: boolean;
    wsPathDirId?: number[];
    dir?: number;
    hiddenScroll?: boolean;
}

export interface IDialogComponentProperties<OptionsType, SharedStateType> extends IDialogConfig {
    setConfirmValueGetter: (getter: () => any) => void;
    unsetConfirmValueGetter: (getter?: () => any) => void;

    options: OptionsType;

    active: boolean;
    sharedState: SharedStateType;

    onSharedStateUpdate: (value: SharedStateType) => void;
    setContainerSize: () => void;
    close: () => void;

    glob: IGlob;
}

export type IDialogComponent = React.ComponentClass<any> | ((params) => React.JSX.Element);

export interface IDialogConfigCustom<ComponentOptionsType, HeaderOptionsType, SharedStateType> extends IDialogConfig {
    getInitialSharedState?: () => SharedStateType;

    content?: string | React.JSX.Element | Element;

    confirm?: boolean;
    confirmTitle?: string;

    deny?: boolean;
    cancelTitle?: string;

    cancel?: boolean;
    closeTitle?: string;

    Component?: IDialogComponent;
    componentOptions?: ComponentOptionsType;
    componentProps?: any;

    HeaderComponent?: React.ComponentClass<IDialogComponentProperties<HeaderOptionsType, SharedStateType>>;
    headerComponentOptions?: HeaderOptionsType;
    headerComponentProps?: any;

    FooterComponent?;
    footerComponentOptions?;
    footerComponentProps?;

    dialogClassName?: string;

    fullHeight?: boolean;
}

export enum DialogConstants {
    CREATE_DIALOG = 'create-dialog',
    DESTROY_DIALOG = 'destroy-dialog',
    ACTIVATE_DIALOG = 'activate-dialog',

    CREATE_POPUP = 'create-popup',
    DESTROY_POPUP = 'destroy-popop'
}

export interface IDialogAction extends Action<DialogConstants> {}

export function checkAction<T extends IDialogAction>(action: IDialogAction, id: DialogConstants): action is T {
    return action.actionType === id;
}

export module I {
    export interface CREATE_DIALOG extends IDialogAction {
        title: string;
        text?: string;
        success: (...args) => any;
        fail: (...args) => any;
        cancel: (...args) => any;
        type: string | DialogType;
        config:
            | IDialogConfigConfirm
            | IDialogConfigWorkspaceDirSelect
            | IDialogConfigTemplateDirSelect
            | IDialogConfigCustom<any, any, any>
            | IDialogConfigFullScreen;
    }

    export interface DESTROY_DIALOG extends IDialogAction {
        id: string;
    }

    export interface ACTIVATE_DIALOG extends IDialogAction {
        id: string;
    }

    export interface CREATE_POPUP extends IDialogAction {
        type: string;
        options: IPopupOptions;
    }

    export interface DESTROY_POPUP extends IDialogAction {
        id: string;
    }
}

export class DialogActions {
    constructor(private dispatcher: DialogDispatcher) {}

    public static get(dispatcher: DialogDispatcher) {
        return new DialogActions(dispatcher);
    }

    public createDialog(
        title: string,
        text: string,
        success: (...args) => any,
        fail: (...args) => any,
        cancel: (...args) => any,
        type: string | DialogType,
        config:
            | IDialogConfigConfirm
            | IDialogConfigWorkspaceDirSelect
            | IDialogConfigTemplateDirSelect
            | IDialogConfigCustom<any, any, any>
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            text: text,
            success: success,
            fail: fail,
            cancel: cancel,
            type: type,
            config: config
        });
    }

    public createDialogConfirm(
        title: string,
        text: string,
        success: (...args) => any,
        fail: (...args) => any,
        cancel: (...args) => any,
        config: IDialogConfigConfirm
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            text: text,
            success: success,
            fail: fail,
            cancel: cancel,
            type: DialogType.CONFIRM,
            config: config
        });
    }

    public createDialogWorkspaceDirSelect(
        title: string,
        success: (...args) => any,
        fail: (...args) => any,
        config: IDialogConfigWorkspaceDirSelect
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            success: success,
            fail: fail,
            cancel: fail,
            type: DialogType.WORKSPACE_DIR_SELECT,
            config: config
        });
    }

    public createDialogTemplateDirSelect(
        title: string,
        success: (...args) => any,
        fail: (...args) => any,
        config: IDialogConfigTemplateDirSelect
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            success: success,
            fail: fail,
            cancel: fail,
            type: DialogType.TEMPLATE_DIR_SELECT,
            config: config
        });
    }

    public createDialogCustom<ComponentOptionsType, HeaderOptionsType, SharedStateType>(
        title: string,
        success: (...args) => any,
        fail: (...args) => any,
        cancel: (...args) => any,
        config: IDialogConfigCustom<ComponentOptionsType, HeaderOptionsType, SharedStateType>
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            success: success,
            fail: fail,
            cancel: cancel,
            type: DialogType.CUSTOM,
            config: config
        });
    }

    public createNewDialog<ComponentOptionsType, HeaderOptionsType = any, SharedStateType = any>(
        title: string,
        success: (...args) => any,
        fail: (...args) => any,
        cancel: (...args) => any,
        config: IDialogConfigCustom<ComponentOptionsType, HeaderOptionsType, SharedStateType>
    ) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: title,
            success: success,
            fail: fail,
            cancel: cancel,
            type: DialogType.NEW_CUSTOM,
            config: config
        });
    }

    public createDialogFullScreen(cancel: (...args) => any, config: IDialogConfigFullScreen) {
        this.dispatcher.handleViewAction<I.CREATE_DIALOG>({
            actionType: DialogConstants.CREATE_DIALOG,

            title: '',
            text: '',
            success: () => {},
            fail: () => {},
            cancel: cancel,
            type: DialogType.FULL_SCREEN,
            config: config
        });
    }

    public destroyDialog(id: string) {
        this.dispatcher.handleViewAction<I.DESTROY_DIALOG>({
            actionType: DialogConstants.DESTROY_DIALOG,
            id: id
        });
    }

    public activateDialog(id: string) {
        this.dispatcher.handleViewAction<I.ACTIVATE_DIALOG>({
            actionType: DialogConstants.ACTIVATE_DIALOG,
            id: id
        });
    }

    public createPopup<TOptions extends IPopupOptions>(type: string, options: TOptions) {
        this.dispatcher.handleViewAction<I.CREATE_POPUP>({
            actionType: DialogConstants.CREATE_POPUP,
            type: type,
            options: options
        });
    }

    public destroyPopup(id: string) {
        this.dispatcher.handleViewAction<I.DESTROY_POPUP>({
            actionType: DialogConstants.DESTROY_POPUP,
            id: id
        });
    }
}

export class DialogDispatcher extends BaseDispatcher {
    /**
     * A bridge function between the views and the dispatcher, marking the action
     * as a view action.  Another variant here could be handleServerAction.
     */
    public handleViewAction<T>(action: T) {
        this.dispatch({
            source: 'VIEW_ACTION',
            action: action
        });
    }

    public static get() {
        return new DialogDispatcher();
    }
}
